import { useEffect, useState } from 'react';
import './ContactForm.css';

const ContactForm = ({ idPrefix = 'cf', presetSubject = '', presetMessage = '' }) => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    company: '',
    subject: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  useEffect(() => {
    if (!presetSubject) return;
    setFormData((prev) => ({ ...prev, subject: presetSubject }));
  }, [presetSubject]);

  useEffect(() => {
    if (!presetMessage) return;
    setFormData((prev) => ({ ...prev, message: presetMessage }));
  }, [presetMessage]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      const data = await response.json();

      if (response.ok) {
        setSubmitStatus({
          type: 'success',
          message: data.message || "Thank you! We'll get back to you soon.",
          email: formData.email,
        });
        setFormData({ firstName: '', lastName: '', email: '', company: '', subject: '', message: '' });
      } else {
        setSubmitStatus({
          type: 'error',
          message: data.error || 'Failed to send message. Please try again.',
        });
      }
    } catch {
      setSubmitStatus({
        type: 'error',
        message: 'Connection error. Please check your internet connection and try again.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitStatus?.type === 'success') {
    return (
      <div className="ctp-success">
        <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
          <circle cx="24" cy="24" r="23" stroke="var(--accent)" strokeWidth="2" />
          <path
            d="M14 24l7 7 13-14"
            stroke="var(--accent)"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        <h3>Message sent.</h3>
        <p>
          We'll get back to you at <strong>{submitStatus.email}</strong> within 24 hours.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="ctp-form">
      {submitStatus?.type === 'error' && (
        <div className="ctp-form-error">{submitStatus.message}</div>
      )}

      <div className="ctp-form-row">
        <div className="ctp-field">
          <label htmlFor={`${idPrefix}-firstName`}>
            First Name <span>*</span>
          </label>
          <input
            type="text"
            id={`${idPrefix}-firstName`}
            name="firstName"
            value={formData.firstName}
            onChange={handleInputChange}
            required
            placeholder="First name"
          />
        </div>
        <div className="ctp-field">
          <label htmlFor={`${idPrefix}-lastName`}>
            Last Name <span>*</span>
          </label>
          <input
            type="text"
            id={`${idPrefix}-lastName`}
            name="lastName"
            value={formData.lastName}
            onChange={handleInputChange}
            required
            placeholder="Last name"
          />
        </div>
      </div>

      <div className="ctp-field">
        <label htmlFor={`${idPrefix}-email`}>
          Email Address <span>*</span>
        </label>
        <input
          type="email"
          id={`${idPrefix}-email`}
          name="email"
          value={formData.email}
          onChange={handleInputChange}
          required
          placeholder="your.email@company.com"
        />
      </div>

      <div className="ctp-field">
        <label htmlFor={`${idPrefix}-company`}>Company / Organisation</label>
        <input
          type="text"
          id={`${idPrefix}-company`}
          name="company"
          value={formData.company}
          onChange={handleInputChange}
          placeholder="Where do you work?"
        />
      </div>

      <div className="ctp-field">
        <label htmlFor={`${idPrefix}-subject`}>
          Subject <span>*</span>
        </label>
        <select
          id={`${idPrefix}-subject`}
          name="subject"
          value={formData.subject}
          onChange={handleInputChange}
          required
        >
          <option value="" disabled>Select an option</option>
          <option value="general-inquiry">General Inquiry</option>
          <option value="partnership">Partnership</option>
          <option value="support">Support</option>
          <option value="career">Career Opportunities</option>
          <option value="media">Media & Press</option>
          <option value="other">Other</option>
        </select>
      </div>

      <div className="ctp-field">
        <label htmlFor={`${idPrefix}-message`}>
          Message <span>*</span>
        </label>
        <textarea
          id={`${idPrefix}-message`}
          name="message"
          rows="5"
          value={formData.message}
          onChange={handleInputChange}
          required
          placeholder="Share what's on your mind…"
        />
      </div>

      <button type="submit" className="ctp-submit" disabled={isSubmitting}>
        {isSubmitting ? (
          <>
            <span className="ctp-spinner" />
            Sending…
          </>
        ) : (
          'Send message'
        )}
      </button>

      <p className="ctp-privacy">
        We respect your privacy and will never share your information.
      </p>
    </form>
  );
};

export default ContactForm;
